'use client';

import { useLocale, useTranslations } from 'next-intl';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { API_UNAVAILABLE_SIGNAL, SESSION_EXPIRED_SIGNAL } from '@/lib/auth/gate-signals';

/**
 * Shared view for route-level `error.tsx` boundaries. Without these
 * boundaries, any unhandled error (including a rejected server-action POST —
 * dropped connection, aborted upload) falls through to Next's built-in
 * "This page couldn't load" page, wiping all client state on the route.
 */
export function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('errors');
  const locale = useLocale();
  React.useEffect(() => {
    console.error(error);
  }, [error]);

  // The proxy's answers to a server action it could not let through — see
  // `gate-signals.ts`. Neither is a fault in the page, so neither gets the
  // stack trace or the "an error occurred" headline.
  if (error.message === SESSION_EXPIRED_SIGNAL) {
    const signIn = () => {
      const next = window.location.pathname + window.location.search;
      window.location.assign(`/${locale}/login?next=${encodeURIComponent(next)}`);
    };
    return (
      <SignalCard title={t('boundary.sessionExpiredTitle')} description={t('boundary.sessionExpiredDescription')}>
        <Button variant="primary" onClick={signIn}>
          {t('boundary.signIn')}
        </Button>
      </SignalCard>
    );
  }
  if (error.message === API_UNAVAILABLE_SIGNAL) {
    return (
      <SignalCard title={t('boundary.unavailableTitle')} description={t('boundary.unavailableDescription')}>
        <Button variant="primary" onClick={reset}>
          {t('boundary.retry')}
        </Button>
      </SignalCard>
    );
  }

  return (
    <div className="flex min-h-[60dvh] items-center justify-center p-6">
      <section className="w-full max-w-md rounded-xl border border-danger/30 bg-danger/10 p-6 text-center">
        <h2 className="text-base font-semibold text-foreground">{t('boundary.title')}</h2>
        <p className="mt-2 text-sm text-foreground-muted">{t('boundary.description')}</p>
        {/*
          Both identifiers, always — and they say different things.

          A `digest` means the error was thrown on the SERVER: Next redacted
          `message` to a fixed string before it ever reached this component, and
          the real one is in `docker compose logs web`, keyed by this same hash
          (see `src/instrumentation.ts`). No digest means it was thrown on the
          CLIENT, where `message` is un-redacted and is the actual fault.

          Rendering only the digest — as this did — left the client case with no
          identifier at all: an operator saw two sentences of generic prose and
          could not say what broke, or even which side of the wire broke it.
        */}
        {error.digest ? (
          <p className="mt-2 font-mono text-[11px] text-foreground-subtle">
            server · {error.digest}
          </p>
        ) : null}
        {error.message ? (
          <p className="mt-2 break-words text-left font-mono text-[11px] text-foreground-subtle">
            {error.digest ? null : 'client · '}
            {error.message}
          </p>
        ) : null}
        {/*
          The stack, on the page and selectable, because the browser console is
          not a reliable place to read one: it collapses repeated frames, and a
          production client stack arrives as `0_vhltqso5g2a.js:1:211278` plus a
          run of "NextJS 18" — unreadable without the source maps that
          `next.config.ts` now emits. Rendered here it can simply be copied.

          Plain `<details>` with no `onToggle`: the bug that made this page
          necessary was a `<details>` handler reading `e.currentTarget` from
          inside a state updater. Nothing to record here, so nothing to get
          wrong.
        */}
        {error.stack ? (
          <details className="mt-3 text-left">
            <summary className="cursor-pointer text-[11px] text-foreground-subtle">
              {t('boundary.stack')}
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-surface-muted/40 p-2 font-mono text-[10px] leading-relaxed text-foreground-subtle">
              {error.stack}
            </pre>
          </details>
        ) : null}
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button variant="primary" onClick={reset}>
            {t('boundary.retry')}
          </Button>
          <Button variant="ghost" onClick={() => window.location.reload()}>
            {t('boundary.reload')}
          </Button>
        </div>
      </section>
    </div>
  );
}

function SignalCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-[60dvh] items-center justify-center p-6">
      <section className="w-full max-w-md rounded-xl border border-warning/30 bg-warning/10 p-6 text-center">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="mt-2 text-sm text-foreground-muted">{description}</p>
        <div className="mt-4 flex items-center justify-center gap-2">{children}</div>
      </section>
    </div>
  );
}
